<script setup lang="ts">
import { UiCard, UiButton, UiText, UiIcon } from "@shopexperts/shared";
import DragHandle from "@/components/common/DragHandle.vue";
import type { PackagedService } from "./types/listingModels";
import { ref, watchEffect } from "vue";

const props = defineProps<{
    item: PackagedService;
    index?: number;
}>();

const emit = defineEmits<{
    (e: 'edit'): void;
    (e: 'delete'): void;
}>();

function onEditClick() {
    emit('edit');
}

function onDeleteClick() {
    emit('delete');
}

const previewUrl = ref<string | null>(null);

watchEffect(async () => {
    const item = props.item;
    const image = item.thumbnail 
    if (!image) return previewUrl.value = null;
    if (typeof image === 'string') return previewUrl.value = image;
    if (image.url) return previewUrl.value = image.url as string;
    if (image.file && image.file instanceof File) {
        const reader = new FileReader();
        console.log(`[PackagedServiceCard] reader`, reader);
        reader.onload = () => {
            console.log(`[PackagedServiceCard] reader.onload`, reader.result);
            previewUrl.value = reader.result as string;
        };
        reader.readAsDataURL(image.file);
    }
});


</script>

<template>
    <UiCard padding="md" class="relative draggable-item pr-8 md:pr-0">
        <div class="grid grid-cols-1 md:grid-cols-[360px_1fr] gap-4 items-stretch">
            <div class="bg-gray-100 rounded overflow-hidden aspect-video w-full max-w-full">
                <img v-if="previewUrl" :src="previewUrl" class="w-full h-full object-cover max-w-full max-h-full" />
                <div v-else class="w-full h-full flex items-center justify-center">
                    <UiIcon name="image-placeholder" class="w-16 h-16 text-se-gray-700" />
                </div>
            </div>
            <div class="flex flex-col justify-between gap-4 min-w-0">
                <div class="flex flex-col gap-1">
                    <UiText as="h6" class="text-se-gray-700"
                        :text="`Packaged Service Name #${(props.index ?? 0) + 1}`" />
                    <UiText as="h5" class="font-semibold" :text="props.item.service_name" />
                </div>
                <div class="flex flex-col gap-1">
                    <UiText as="span" class="text-se-gray-700" text="Starting Price" />
                    <UiText as="p" class="font-semibold" :text="`$${props.item.starting_price.toFixed(2)}`" />
                </div>
                <div class="flex items-center gap-4">
                    <UiButton variant="outline" shape="icon" @click="onEditClick" no-py class="p-1.5">
                        <template #before_label>
                            <UiIcon name="pencil" class="w-4.5 h-4.5" />
                        </template>
                    </UiButton>
                    <UiButton variant="outline" shape="icon" @click="onDeleteClick" no-py class="p-1.5">
                        <template #before_label>
                            <UiIcon name="delete" class="w-4.5 h-4.5" />
                        </template>
                    </UiButton>
                    <DragHandle />
                </div>
            </div>
        </div>
    </UiCard>
</template>
